import type { FC, ReactNode } from 'react'
import type {
    AcademicData,
    AcademicYear,
    ActionResult,
    AppSettings,
    AppUser,
    AttendanceRecord,
    AttendanceStatus,
    ClassRoom,
    Gender,
    GradeRecord,
    GradeType,
    JournalEntry,
    Semester,
    Student,
    Subject
} from '@/types'

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState
} from 'react'

import { createSeedData } from '@/data/seed'
import { DEMO_TODAY } from '@/lib/dates'
import { compareText, createId, normalizeSpaces, sortByName } from '@/lib/academic'
import { DEFAULT_KKM } from '@/constants/academic'

// In-memory store for the sample data. It mirrors the rules of the original
// application; changes live until the page is reloaded.

type SaveAttendanceInput = {
    classId: string
    date: string
    statuses: Record<string, AttendanceStatus>
}

type BulkAttendanceInput = {
    classId: string
    date: string
    status: AttendanceStatus
    mode: 'all' | 'empty'
}

type GradeScope = {
    classId: string
    subjectId: string
    type: GradeType
    date: string
}

type SaveGradesInput = GradeScope & {
    kkm: number | null
    scores: Record<string, number | null>
}

type ImportGradesInput = GradeScope & {
    rows: { line: number, nis: string, score: string }[]
}

type SaveJournalInput = {
    id?: string
    classId: string
    date: string
    period: string
    start: string
    end: string
    material: string
}

type SaveStudentInput = {
    id?: string
    nis: string
    name: string
    gender: Gender | null
    classId: string | null
    newClassName: string
}

export type ImportStudentRow = {
    nis: string
    name: string
    gender: Gender | null
    className: string
    status: AttendanceStatus | null
}

type SaveYearInput = {
    id?: string
    name: string
    semester: Semester
    startDate: string | null
    endDate: string | null
}

type SaveUserInput = {
    id?: string
    name: string
    username: string
    active: boolean
    password: string
}

type BrandingInput = Pick<AppSettings, 'appName' | 'tagline' | 'logoSize' | 'showLogo'> & {
    logoUrl?: string | null
}

export type AcademicActions = {
    selectYear: (id: string) => ActionResult
    saveAttendance: (input: SaveAttendanceInput) => ActionResult
    bulkAttendance: (input: BulkAttendanceInput) => ActionResult
    deleteStudentAttendance: (input: { studentId: string, date: string }) => ActionResult
    deleteAttendance: (id: string) => ActionResult
    deleteAttendanceDate: (input: { classId: string, date: string }) => ActionResult
    saveGrades: (input: SaveGradesInput) => ActionResult
    importGrades: (input: ImportGradesInput) => ActionResult
    deleteGrade: (id: string) => ActionResult
    deleteGradeDate: (input: GradeScope) => ActionResult
    saveJournal: (input: SaveJournalInput) => ActionResult
    deleteJournal: (id: string) => ActionResult
    saveSubject: (input: { id?: string, name: string, kkm: number | null }) => ActionResult
    deleteSubject: (id: string) => ActionResult
    assignSubject: (input: { classId: string, subjectId: string, teacherId: string | null }) => ActionResult
    unassignSubject: (id: string) => ActionResult
    saveStudent: (input: SaveStudentInput) => ActionResult
    deleteStudent: (id: string) => ActionResult
    importStudents: (rows: ImportStudentRow[], skipped: number) => ActionResult & { classId?: string }
    saveYear: (input: SaveYearInput) => ActionResult
    activateYear: (id: string) => ActionResult
    deleteYear: (id: string) => ActionResult
    saveUser: (input: SaveUserInput) => ActionResult
    deleteUser: (id: string) => ActionResult
    saveBranding: (input: BrandingInput) => ActionResult
    removeLogo: () => ActionResult
}

type AcademicContextValue = {
    data: AcademicData
    attendance: AttendanceRecord[]
    grades: GradeRecord[]
    journals: JournalEntry[]
    classes: ClassRoom[]
    subjects: Subject[]
    students: Student[]
    users: AppUser[]
    currentUser: AppUser
    selectedYear: AcademicYear | undefined
    classById: Map<string, ClassRoom>
    subjectById: Map<string, Subject>
    studentById: Map<string, Student>
    userById: Map<string, AppUser>
    actions: AcademicActions
}

const AcademicContext = createContext<AcademicContextValue | null>(null)

const DEMO_IP = '10.12.4.21'

const PERIOD_PATTERN = /^(?:[1-9]|1[0-9]|20)(?:-(?:[1-9]|1[0-9]|20))?$/

const fail = (error: string): ActionResult => ({ ok: false, error })

const done = (message: string): ActionResult => ({ ok: true, message })

export const semesterLabel = (year: Pick<AcademicYear, 'name' | 'semester'>): string => {
    return `${year.name} · Semester ${year.semester}`
}

export const AcademicProvider: FC<{ children: ReactNode }> = ({ children }): ReactNode => {
    const store = useRef<AcademicData | null>(null)

    if (store.current === null) store.current = createSeedData()

    const [data, setData] = useState<AcademicData>(() => store.current as AcademicData)

    const read = useCallback((): AcademicData => store.current as AcademicData, [])

    const commit = useCallback((next: AcademicData): void => {
        store.current = next
        setData(next)
    }, [])

    const withLog = useCallback((next: AcademicData, action: string, module: string, description: string): AcademicData => {
        return {
            ...next,
            logs: [
                {
                    id: createId('log'),
                    userId: next.currentUserId,
                    action,
                    module,
                    description,
                    ip: DEMO_IP,
                    createdAt: new Date().toISOString()
                },
                ...next.logs
            ]
        }
    }, [])

    const actions = useMemo<AcademicActions>(() => {
        const className = (state: AcademicData, id: string): string => {
            return state.classes.find(item => item.id === id)?.name ?? '-'
        }

        return {
            selectYear: (id) => {
                const state = read()
                const year = state.years.find(item => item.id === id)

                if (!year) return fail('Tahun ajaran tidak ditemukan.')

                commit({ ...state, selectedYearId: id })

                return done(`Tahun ajaran aktif: ${semesterLabel(year)}`)
            },

            saveAttendance: ({ classId, date, statuses }) => {
                const state = read()
                const attendance = [...state.attendance]

                Object.entries(statuses).forEach(([studentId, status]) => {
                    if (!state.students.some(item => item.id === studentId && item.classId === classId)) return

                    const index = attendance.findIndex(record => record.studentId === studentId && record.date === date)

                    if (index >= 0) attendance[index] = { ...attendance[index], classId, status, yearId: state.selectedYearId }
                    else attendance.push({ id: createId('abs'), yearId: state.selectedYearId, studentId, classId, date, status })
                })

                commit(withLog({ ...state, attendance }, 'save', 'absensi', `Menyimpan absensi kelas ${className(state, classId)}`))

                return done('Absensi berhasil disimpan.')
            },

            bulkAttendance: ({ classId, date, status, mode }) => {
                const state = read()
                const attendance = [...state.attendance]
                let count = 0

                state.students.filter(item => item.classId === classId).forEach(student => {
                    const index = attendance.findIndex(record => record.studentId === student.id && record.date === date)

                    if (mode === 'empty' && index >= 0) return

                    if (index >= 0) attendance[index] = { ...attendance[index], classId, status, yearId: state.selectedYearId }
                    else attendance.push({ id: createId('abs'), yearId: state.selectedYearId, studentId: student.id, classId, date, status })

                    count += 1
                })

                commit(withLog({ ...state, attendance }, 'bulk', 'absensi', `Bulk absensi ${status} untuk ${count} siswa`))

                return done(`Absensi massal diterapkan ke ${count} siswa.`)
            },

            deleteStudentAttendance: ({ studentId, date }) => {
                const state = read()

                commit({ ...state, attendance: state.attendance.filter(item => !(item.studentId === studentId && item.date === date)) })

                return done('Catatan absensi siswa dihapus.')
            },

            deleteAttendance: (id) => {
                const state = read()

                commit({ ...state, attendance: state.attendance.filter(item => item.id !== id) })

                return done('Catatan absensi dihapus.')
            },

            deleteAttendanceDate: ({ classId, date }) => {
                const state = read()
                const attendance = state.attendance.filter(item => !(item.classId === classId && item.date === date))
                const removed = state.attendance.length - attendance.length

                commit({ ...state, attendance })

                return done(removed > 0
                    ? 'Seluruh absensi pada tanggal tersebut berhasil dihapus.'
                    : 'Tidak ada catatan absensi pada tanggal tersebut.')
            },

            saveGrades: ({ classId, subjectId, type, date, kkm, scores }) => {
                const state = read()

                if (kkm !== null && (kkm < 0 || kkm > 100)) return fail('Nilai KKM harus berada di antara 0 sampai 100.')

                const invalid = Object.values(scores).some(score => score !== null && (score < 0 || score > 100))

                if (invalid) return fail('Setiap nilai harus berada di antara 0 sampai 100.')

                let grades = [...state.grades]

                Object.entries(scores).forEach(([studentId, score]) => {
                    if (!state.students.some(item => item.id === studentId && item.classId === classId)) return

                    const matches = (record: GradeRecord): boolean => record.studentId === studentId && record.subjectId === subjectId && record.type === type && record.date === date

                    if (score === null) {
                        grades = grades.filter(record => !matches(record))

                        return
                    }

                    const index = grades.findIndex(matches)

                    if (index >= 0) grades[index] = { ...grades[index], score, kkm: kkm ?? grades[index].kkm, yearId: state.selectedYearId }
                    else grades.push({ id: createId('nil'), yearId: state.selectedYearId, studentId, subjectId, type, date, score, kkm })
                })

                if (kkm !== null) {
                    grades = grades.map(record => record.subjectId === subjectId && record.type === type && record.date === date ? { ...record, kkm } : record)
                }

                commit(withLog({ ...state, grades }, 'save', 'nilai', 'Menyimpan nilai'))

                return done('Nilai berhasil disimpan.')
            },

            importGrades: ({ classId, subjectId, type, date, rows }) => {
                const state = read()
                const classStudents = state.students.filter(item => item.classId === classId)
                const prepared: { studentId: string, score: number }[] = []

                for (const row of rows) {
                    const student = classStudents.find(item => item.nis === row.nis.trim())
                    const score = Number(row.score.replace(',', '.'))

                    if (!student) return fail(`Impor ditolak: baris ${row.line}, NIS ${row.nis} tidak ditemukan di kelas ${className(state, classId)}.`)
                    if (row.score.trim() === '' || !Number.isFinite(score) || score < 0 || score > 100) {
                        return fail(`Impor ditolak: baris ${row.line}, nilai harus berada di antara 0 sampai 100.`)
                    }

                    prepared.push({ studentId: student.id, score })
                }

                if (prepared.length === 0) return fail('Tidak ada nilai yang bisa diimpor. Isi kolom NIS dan Nilai pada template.')

                const subject = state.subjects.find(item => item.id === subjectId)
                const grades = [...state.grades]

                prepared.forEach(({ studentId, score }) => {
                    const index = grades.findIndex(record => record.studentId === studentId && record.subjectId === subjectId && record.type === type && record.date === date)

                    if (index >= 0) grades[index] = { ...grades[index], score }
                    else grades.push({ id: createId('nil'), yearId: state.selectedYearId, studentId, subjectId, type, date, score, kkm: subject?.kkm ?? DEFAULT_KKM })
                })

                commit(withLog({ ...state, grades }, 'import', 'nilai', `Mengimpor ${prepared.length} nilai`))

                return done(`${prepared.length} nilai berhasil diimpor.`)
            },

            deleteGrade: (id) => {
                const state = read()

                commit({ ...state, grades: state.grades.filter(item => item.id !== id) })

                return done('Catatan nilai dihapus.')
            },

            deleteGradeDate: ({ classId, subjectId, type, date }) => {
                const state = read()
                const classStudentIds = new Set(state.students.filter(item => item.classId === classId).map(item => item.id))
                const grades = state.grades.filter(item => !(classStudentIds.has(item.studentId) && item.subjectId === subjectId && item.type === type && item.date === date))
                const removed = state.grades.length - grades.length

                commit({ ...state, grades })

                return done(removed > 0
                    ? 'Seluruh nilai pada tanggal tersebut berhasil dihapus.'
                    : 'Tidak ada nilai pada tanggal tersebut.')
            },

            saveJournal: (input) => {
                const state = read()
                const period = input.period.replace(/\s+/g, '').replace(/[–—]/g, '-')
                const material = input.material.trim()

                if (!PERIOD_PATTERN.test(period)) return fail('Jam ke diisi angka 1 sampai 20 atau rentang seperti 1-2.')

                const [from, to] = period.split('-').map(Number)

                if (to !== undefined && from > to) return fail('Rentang jam pelajaran harus berurutan.')
                if (!input.start || !input.end) return fail('Isi waktu mulai dan waktu selesai.')
                if (input.end <= input.start) return fail('Waktu selesai harus setelah waktu mulai.')
                if (!material) return fail('Materi pembelajaran wajib diisi.')
                if (material.length > 2000) return fail('Materi pembelajaran wajib diisi maksimal 2.000 karakter.')

                const duplicate = state.journals.some(item => item.id !== input.id && item.classId === input.classId && item.date === input.date && item.period === period)

                if (duplicate) return fail('Jurnal untuk kelas, tanggal, dan jam tersebut sudah ada. Buka jurnal yang ada untuk mengubahnya.')

                const entry: JournalEntry = {
                    id: input.id ?? createId('jur'),
                    yearId: state.selectedYearId,
                    classId: input.classId,
                    date: input.date,
                    period,
                    start: input.start,
                    end: input.end,
                    material
                }

                const journals = input.id
                    ? state.journals.map(item => item.id === input.id ? entry : item)
                    : [...state.journals, entry]

                commit({ ...state, journals })

                return done('Jurnal mengajar berhasil disimpan.')
            },

            deleteJournal: (id) => {
                const state = read()

                commit({ ...state, journals: state.journals.filter(item => item.id !== id) })

                return done('Catatan jurnal dihapus.')
            },

            saveSubject: ({ id, name, kkm }) => {
                const state = read()
                const cleanName = normalizeSpaces(name)

                if (!cleanName) return fail('Nama mata pelajaran wajib diisi.')
                if (cleanName.length > 100) return fail('Nama mata pelajaran maksimal 100 karakter.')
                if (kkm !== null && (!Number.isFinite(kkm) || kkm < 0 || kkm > 100)) return fail('Nilai KKM harus berada di antara 0 sampai 100.')
                if (state.subjects.some(item => item.id !== id && compareText(item.name, cleanName) === 0)) {
                    return fail('Nama mata pelajaran sudah terdaftar.')
                }

                const value = kkm ?? DEFAULT_KKM
                const subjects = id
                    ? state.subjects.map(item => item.id === id ? { ...item, name: cleanName, kkm: value } : item)
                    : [...state.subjects, { id: createId('mpl'), name: cleanName, kkm: value }]

                commit({ ...state, subjects })

                return done('Mata pelajaran berhasil disimpan.')
            },

            deleteSubject: (id) => {
                const state = read()

                commit({
                    ...state,
                    subjects: state.subjects.filter(item => item.id !== id),
                    grades: state.grades.filter(item => item.subjectId !== id),
                    classSubjects: state.classSubjects.filter(item => item.subjectId !== id)
                })

                return done('Mata pelajaran dan nilai terkait berhasil dihapus.')
            },

            assignSubject: ({ classId, subjectId, teacherId }) => {
                const state = read()

                if (!classId || !subjectId) return fail('Pilih kelas dan mata pelajaran.')

                const existing = state.classSubjects.find(item => item.classId === classId && item.subjectId === subjectId)
                const classSubjects = existing
                    ? state.classSubjects.map(item => item.id === existing.id ? { ...item, teacherId } : item)
                    : [...state.classSubjects, { id: createId('km'), classId, subjectId, teacherId }]

                commit(withLog({ ...state, classSubjects }, 'assign', 'mapel', 'Assign mapel ke kelas'))

                return done('Mapel berhasil di-assign ke kelas.')
            },

            unassignSubject: (id) => {
                const state = read()

                commit({ ...state, classSubjects: state.classSubjects.filter(item => item.id !== id) })

                return done('Assign mapel dihapus.')
            },

            saveStudent: ({ id, nis, name, gender, classId, newClassName }) => {
                const state = read()
                const cleanNis = nis.trim()
                const cleanName = normalizeSpaces(name)
                const cleanClass = normalizeSpaces(newClassName)

                if (!cleanNis || !/[\p{L}\p{N}]/u.test(cleanNis)) return fail('NIS wajib diisi dengan huruf atau angka.')
                if (cleanNis.length > 30) return fail('NIS maksimal 30 karakter.')
                if (!cleanName || !/\p{L}/u.test(cleanName)) return fail('Nama lengkap wajib diisi.')
                if (cleanName.length > 100) return fail('Nama lengkap maksimal 100 karakter.')
                if (cleanClass.length > 50) return fail('Nama kelas maksimal 50 karakter.')

                let classes = state.classes
                let targetClassId: string | null = null

                if (cleanClass) {
                    const existing = classes.find(item => item.name === cleanClass)

                    if (existing) {
                        targetClassId = existing.id
                    }
                    else {
                        const created = { id: createId('kls'), name: cleanClass }
                        classes = [...classes, created]
                        targetClassId = created.id
                    }
                }
                else if (classId) {
                    targetClassId = classId
                }
                else if (id) {
                    targetClassId = state.students.find(item => item.id === id)?.classId ?? null
                }

                if (!targetClassId) return fail('Pilih atau tulis nama kelas terlebih dahulu.')

                if (state.students.some(item => item.id !== id && item.classId === targetClassId && item.nis === cleanNis)) {
                    return fail('NIS sudah digunakan di kelas ini.')
                }

                const student: Student = { id: id ?? createId('sis'), classId: targetClassId, nis: cleanNis, name: cleanName, gender }
                const students = id
                    ? state.students.map(item => item.id === id ? student : item)
                    : [...state.students, student]

                commit(withLog({ ...state, classes, students }, 'save', 'siswa', `Menyimpan siswa ${cleanName}`))

                return done('Data siswa berhasil disimpan.')
            },

            deleteStudent: (id) => {
                const state = read()

                commit({
                    ...state,
                    students: state.students.filter(item => item.id !== id),
                    attendance: state.attendance.filter(item => item.studentId !== id),
                    grades: state.grades.filter(item => item.studentId !== id)
                })

                return done('Siswa dan catatan terkait berhasil dihapus.')
            },

            importStudents: (rows, skipped) => {
                const state = read()

                if (rows.length === 0) {
                    return fail('Tidak ada data siswa yang bisa diimpor. Pilih kelas atau sertakan kolom Kelas.')
                }

                let classes = [...state.classes]
                const students = [...state.students]
                const attendance = [...state.attendance]
                const seen = new Set<string>()
                let lastClassId = ''

                for (const row of rows) {
                    const existingClass = classes.find(item => item.name === row.className)
                    const key = `${existingClass?.id ?? row.className}|${row.nis}`
                    const duplicate = existingClass && students.some(item => item.classId === existingClass.id && item.nis === row.nis)

                    if (seen.has(key) || duplicate) {
                        return fail(`Impor dibatalkan: NIS ${row.nis} sudah terdaftar atau berulang di kelas tujuan.`)
                    }

                    seen.add(key)
                }

                rows.forEach(row => {
                    let target = classes.find(item => item.name === row.className)

                    if (!target) {
                        target = { id: createId('kls'), name: row.className }
                        classes = [...classes, target]
                    }

                    const student: Student = { id: createId('sis'), classId: target.id, nis: row.nis, name: row.name, gender: row.gender }
                    students.push(student)
                    lastClassId = target.id

                    if (row.status) {
                        attendance.push({ id: createId('abs'), yearId: state.selectedYearId, studentId: student.id, classId: target.id, date: DEMO_TODAY, status: row.status })
                    }
                })

                let message = `${rows.length} siswa berhasil diimpor. Kelas pada file dibuat otomatis jika belum tersedia.`

                if (skipped > 0) message += ` ${skipped} baris tidak valid dilewati.`

                commit(withLog({ ...state, classes, students, attendance }, 'import', 'siswa', `Mengimpor ${rows.length} siswa`))

                return { ok: true, message, classId: lastClassId }
            },

            saveYear: ({ id, name, semester, startDate, endDate }) => {
                const state = read()
                const cleanName = name.trim()
                const match = /^(\d{4})\/(\d{4})$/.exec(cleanName)

                if (!match) return fail('Format tahun ajaran tidak valid. Contoh: 2026/2027.')

                const startYear = Number(match[1])

                if (Number(match[2]) !== startYear + 1) return fail('Tahun ajaran harus berurutan, misalnya 2026/2027.')
                if (startDate && endDate && endDate < startDate) return fail('Tanggal selesai harus sama atau setelah tanggal mulai.')

                if (!id) {
                    const hasActive = state.years.some(item => item.active)
                    const currentSemester: Semester = Number(DEMO_TODAY.slice(5, 7)) >= 7 ? '1' : '2'
                    const semesters: Semester[] = ['1', '2']
                    let years = [...state.years]
                    let activeId: string | null = null

                    for (const value of semesters) {
                        const existing = years.find(item => item.name === cleanName && item.semester === value)
                        const shouldActivate = !hasActive && value === currentSemester

                        if (existing) {
                            if (shouldActivate) {
                                years = years.map(item => item.id === existing.id ? { ...item, active: true } : item)
                                activeId = existing.id
                            }

                            continue
                        }

                        const created: AcademicYear = {
                            id: createId('ta'),
                            name: cleanName,
                            semester: value,
                            startDate: value === '1' ? `${startYear}-07-01` : `${startYear + 1}-01-01`,
                            endDate: value === '1' ? `${startYear}-12-31` : `${startYear + 1}-06-30`,
                            active: shouldActivate
                        }

                        years = [...years, created]

                        if (shouldActivate) activeId = created.id
                    }

                    commit(withLog(
                        { ...state, years, selectedYearId: activeId ?? state.selectedYearId },
                        'create',
                        'tahun_ajaran',
                        `Menambah tahun ajaran ${cleanName} dengan Semester 1 dan Semester 2`
                    ))

                    return done('Tahun ajaran ditambahkan dengan Semester 1 dan Semester 2.')
                }

                if (state.years.some(item => item.id !== id && item.name === cleanName && item.semester === semester)) {
                    return fail('Kombinasi tahun ajaran dan semester tersebut sudah ada.')
                }

                commit(withLog(
                    {
                        ...state,
                        years: state.years.map(item => item.id === id ? { ...item, name: cleanName, semester, startDate, endDate } : item)
                    },
                    'update',
                    'tahun_ajaran',
                    `Memperbarui tahun ajaran ${cleanName}`
                ))

                return done('Tahun ajaran diperbarui.')
            },

            activateYear: (id) => {
                const state = read()
                const target = state.years.find(item => item.id === id)

                if (!target) return fail('Data tidak ditemukan.')

                commit(withLog(
                    { ...state, selectedYearId: id, years: state.years.map(item => ({ ...item, active: item.id === id })) },
                    'activate',
                    'tahun_ajaran',
                    `Mengaktifkan tahun ajaran ${semesterLabel(target)}`
                ))

                return done(`Tahun ajaran dijadikan aktif: ${semesterLabel(target)}`)
            },

            deleteYear: (id) => {
                const state = read()
                const target = state.years.find(item => item.id === id)

                if (!target) return fail('Data tidak ditemukan.')
                if (target.active) return fail('Tidak bisa menghapus tahun ajaran yang sedang aktif.')

                const fallback = state.years.find(item => item.active)?.id ?? state.selectedYearId

                commit(withLog(
                    {
                        ...state,
                        years: state.years.filter(item => item.id !== id),
                        selectedYearId: state.selectedYearId === id ? fallback : state.selectedYearId
                    },
                    'delete',
                    'tahun_ajaran',
                    `Menghapus tahun ajaran ${target.name}`
                ))

                return done('Tahun ajaran dihapus.')
            },

            saveUser: ({ id, name, username, active, password }) => {
                const state = read()
                const cleanName = normalizeSpaces(name)
                const cleanUsername = username.trim()

                if (!cleanName) return fail('Nama wajib diisi.')
                if (cleanName.length > 100) return fail('Nama maksimal 100 karakter.')
                if (!cleanUsername) return fail('Username wajib diisi.')
                if (cleanUsername.length > 50) return fail('Username maksimal 50 karakter.')
                if (state.users.some(item => item.id !== id && item.username === cleanUsername)) return fail('Username sudah digunakan.')
                if (!id && password.length < 6) return fail('Password minimal 6 karakter.')
                if (id && password && password.length < 6) return fail('Password minimal 6 karakter.')

                if (id) {
                    commit(withLog(
                        { ...state, users: state.users.map(item => item.id === id ? { ...item, name: cleanName, username: cleanUsername, active } : item) },
                        'update',
                        'pengguna',
                        `Memperbarui pengguna ${cleanUsername}`
                    ))

                    return done('Pengguna diperbarui.')
                }

                commit(withLog(
                    { ...state, users: [...state.users, { id: createId('usr'), name: cleanName, username: cleanUsername, role: 'admin', active }] },
                    'create',
                    'pengguna',
                    `Menambah pengguna ${cleanUsername}`
                ))

                return done('Pengguna ditambahkan.')
            },

            deleteUser: (id) => {
                const state = read()
                const target = state.users.find(item => item.id === id)

                if (id === state.currentUserId) return fail('Tidak bisa menghapus akun sendiri.')
                if (!target) return fail('Pengguna tidak ditemukan.')

                commit(withLog(
                    {
                        ...state,
                        users: state.users.filter(item => item.id !== id),
                        classSubjects: state.classSubjects.map(item => item.teacherId === id ? { ...item, teacherId: null } : item)
                    },
                    'delete',
                    'pengguna',
                    `Menghapus (soft) pengguna ${target.username}`
                ))

                return done('Pengguna dihapus.')
            },

            saveBranding: ({ appName, tagline, logoSize, showLogo, logoUrl }) => {
                const state = read()
                const cleanName = normalizeSpaces(appName)

                if (!cleanName) return fail('Nama aplikasi wajib diisi.')
                if (cleanName.length > 60) return fail('Nama aplikasi maksimal 60 karakter.')
                if (tagline.length > 90) return fail('Deskripsi singkat maksimal 90 karakter.')

                commit({
                    ...state,
                    settings: {
                        ...state.settings,
                        appName: cleanName,
                        tagline: tagline.trim(),
                        logoSize: Math.max(28, Math.min(64, Math.round(logoSize))),
                        showLogo,
                        logoUrl: logoUrl === undefined ? state.settings.logoUrl : logoUrl
                    }
                })

                return done('Tampilan aplikasi berhasil disimpan.')
            },

            removeLogo: () => {
                const state = read()

                commit({ ...state, settings: { ...state.settings, logoUrl: null } })

                return done('Logo kustom berhasil dihapus.')
            }
        }
    }, [commit, read, withLog])

    const value = useMemo<AcademicContextValue>(() => ({
        data,
        attendance: data.attendance,
        grades: data.grades,
        journals: data.journals,
        classes: sortByName(data.classes),
        subjects: sortByName(data.subjects),
        students: sortByName(data.students),
        users: sortByName(data.users),
        currentUser: data.users.find(user => user.id === data.currentUserId) ?? data.users[0],
        selectedYear: data.years.find(year => year.id === data.selectedYearId),
        classById: new Map(data.classes.map(item => [item.id, item])),
        subjectById: new Map(data.subjects.map(item => [item.id, item])),
        studentById: new Map(data.students.map(item => [item.id, item])),
        userById: new Map(data.users.map(item => [item.id, item])),
        actions
    }), [data, actions])

    return (
        <AcademicContext.Provider value={value}>
            {children}
        </AcademicContext.Provider>
    )
}

export const useAcademic = (): AcademicContextValue => {
    const context = useContext(AcademicContext)

    if (!context) throw new Error('useAcademic must be used inside AcademicProvider')

    return context
}
